import { useLoader } from "@react-three/fiber";
import axios from "axios";
import parse from "html-react-parser";
import MDButton from "components/MDButton";
import html2canvas from "html2canvas";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import MiniMap from "leaflet-minimap";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  LinearProgress,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Card, CardBody, CardHeader, CardTitle, Col, Form, Row } from "reactstrap";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { basicURL } from "../../apiManager/APIConfig";
import logo from "../../assets/images/logo.png";
import { openAppConfirm } from "../../components/AppDialogs";
import CustomDrawer from "../../components/CustomDrawer";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { askAIChatCall, getAIMessagesByThreadCall } from "../../redux/api/ai.api";
import { GetSlideByIdCall } from "../../redux/api/title.api";
import { DeleteFile } from "../../redux/api/upload.api";
import { store } from "../../redux/store";
import "./map.css";

const PUBLIC_SLIDE_TYPES = ["his", "emb", "three_d_model", "presentation"];

function escapeHtml(text) {
  if (text == null || typeof text !== "string") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function createSlidePinIcon() {
  // Use explicit asset URLs to avoid broken marker icons in webpack/runtime.
  return L.icon({
    iconRetinaUrl:
      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
}

function ShowTile({ history }) {
  const userData = history?.location?.state?.data?.array;
  const fileInputRef = useRef();
  const { id, lang } = useParams();
  const mapRef = useRef(null); // Ref for storing map instance
  const drawnItemsRef = useRef(null);
  const pendingNewLayerRef = useRef(null);
  const mapViewRef = useRef({
    center: [-10.505, -0.09],
    zoom: 2,
  });

  const content = userData?.content;

  const [info, setInfo] = useState([]);
  const [geoJson, setGeoJson] = useState([]);

  const [showLoader, setShowLoader] = useState(false);
  const [uploadProgress, setUploadPercentage] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState("uploadProgress:");

  const [drawerOpen, setOpenDrawer] = useState(false);
  const [activeDeleteButton, setActiveDeleteButton] = useState(false);
  const [tileURL, setTitleURL] = useState(``);
  const [key, setKey] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiThreadId, setAiThreadId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [capturedImage, setCapturedImage] = useState("");
  const [hasDrawing, setHasDrawing] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [captureGridOpen, setCaptureGridOpen] = useState(false);
  const [gridRows, setGridRows] = useState(6);
  const [gridCols, setGridCols] = useState(6);
  const [currentTile, setCurrentTile] = useState(0);
  const [capturedTiles, setCapturedTiles] = useState({});
  const [captureSessionId, setCaptureSessionId] = useState("");
  const [captureFeedback, setCaptureFeedback] = useState("");
  const [captureBusy, setCaptureBusy] = useState(false);
  const [stitchBusy, setStitchBusy] = useState(false);
  const [stitchJobId, setStitchJobId] = useState("");
  const [stitchStatus, setStitchStatus] = useState("");
  const [stitchPipelinePercent, setStitchPipelinePercent] = useState(0);
  const [stitchPipelinePhase, setStitchPipelinePhase] = useState("");
  const [stitchPipelineMessage, setStitchPipelineMessage] = useState("");
  const [isCaptureSessionHydrated, setIsCaptureSessionHydrated] = useState(false);
  const [needsRetake, setNeedsRetake] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [initialGuideSeen, setInitialGuideSeen] = useState(false);
  const [ghostEnabled, setGhostEnabled] = useState(true);
  const [ghostOpacity, setGhostOpacity] = useState(35);
  const [referenceTile, setReferenceTile] = useState(null);
  const [referenceDirection, setReferenceDirection] = useState(null);
  const [referenceStripUrl, setReferenceStripUrl] = useState("");
  const [liveStripUrl, setLiveStripUrl] = useState("");
  const [overlapScore, setOverlapScore] = useState(null);
  const [overlapHint, setOverlapHint] = useState("");
  const videoRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const fallbackCameraInputRef = useRef(null);
  const liveStripCanvasRef = useRef(null);
  const referenceStripDataRef = useRef(null);
  const captureSessionSaveTimerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const baseImageRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const role = store.getState().auth?.role;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isPublicSlide = PUBLIC_SLIDE_TYPES.includes(
    geoJson?.parentTitle?.type || geoJson?.Titles?.type || geoJson?.titles?.type || ""
  );
  const canEditSlide = !isPublicSlide || role === "admin";
  ////3d modles

  useEffect(() => {
    fetchSlide();
  }, []);
  /////////
  /////////
  function generateUniqueId() {
    return "id-" + Math.random().toString(36).substr(2, 16);
  }
  const fetchSlide = async () => {
    const params = new URLSearchParams(window.location.search);
    const includePrivatePins = params.get("private") === "1";
    const res = await GetSlideByIdCall({
      id,
      lang,
      includePrivatePins,
      ts: Date.now(),
    });
    ////here ......
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    setGeoJson(res);

    setActiveDeleteButton(res?.Files?.[0]?.url ? true : false);
    setTitleURL(`${res?.Files?.[0]?.url}/{z}/{x}/{y}.png`);
    // window.location.reload();
  };
  useEffect(() => {
    const timer = setTimeout(() => {

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (!isEmpty(geoJson) && tileURL && !mapRef.current) {
        const mapContainer = L.DomUtil.get("map");
        if (mapContainer && mapContainer._leaflet_id) {
          // Defensive cleanup for hot reload / rapid state updates.
          mapContainer._leaflet_id = null;
        }
        const map = L.map("map", {
          center: mapViewRef.current.center,
          zoom: mapViewRef.current.zoom,
          maxBounds: [
            [-90, -180],
            [90, 180],
          ],
          maxBoundsViscosity: 1,
        });
        mapRef.current = map;
        map.on("moveend zoomend", () => {
          const center = map.getCenter();
          mapViewRef.current = {
            center: [center.lat, center.lng],
            zoom: map.getZoom(),
          };
        });
        const mainTileLayer = L.tileLayer(tileURL, {
          attribution: '&copy; <a href="https://microlab.ink">Microlab</a> contributors',
          noWrap: true,
          bounds: [
            [-90, -180],
            [90, 180],
          ],
        }).addTo(map);
        // Fix first-render blank main map by forcing Leaflet size recalculation.
        mainTileLayer.on("load", () => {
          setTimeout(() => map.invalidateSize(true), 0);
          setTimeout(() => map.invalidateSize(true), 300);
        });
        //////////
        const minimapLayer = L.tileLayer(tileURL, {
          maxZoom: 0,
          noWrap: true,

          detectRetina: true,
          minZoom: 0,
          bounds: [
            [-90, -180],
            [90, 180],
          ],
          maxNativeZoom: 0,
        });
        ////////
        ////////
        new MiniMap(minimapLayer, {
          zoomLevelFixed: 2,
          position: "bottomleft",
          width: 120,
          height: 120,
          collapsedWidth: 5,
          collapsedHeight: 5,
          aimingRectOptions: {
            color: "#00f",
            weight: 1,
            fillOpacity: 0.1,
          },
        }).addTo(map);
        setTimeout(() => map.invalidateSize(true), 0);
        setTimeout(() => map.invalidateSize(true), 500);

        /////////
        ///////// show the saved pins

        const isStudent = role === "student";
        const params = new URLSearchParams(window.location.search);
        const isPrivateView = params.get("private") === "1";
        // Pins can be edited only when:
        // - user is not a student AND
        // - either this is not a public-core slide, or we're in private overlay view, or the user is admin.
        const canEditPins =
          !isStudent && (isPrivateView || !isPublicSlide || role === "admin");

        (geoJson?.pins || []).forEach((pin, index) => {
          const location = JSON.parse(pin.locations);
          const coordinates = location.data.geometry.coordinates;
          const marker = L.marker([coordinates[1], coordinates[0]], { icon: createSlidePinIcon() }).addTo(map);

          const titleLocalization = pin.pinLocalizations?.find(
            (loc) => loc.field === "title" && loc.language === lang
          );
          const bodyLocalization = pin.pinLocalizations?.find(
            (loc) => loc.field === "body" && loc.language === lang
          );
          const pinTitle = titleLocalization?.value || "Pin";
          const pinBody = bodyLocalization?.value || "";

          if (!canEditPins) {
            // Read-only: show pin details in a popup with close button
            const popupContent = `<div class="pin-popup-content" style="min-width:180px;max-width:280px;">
              <div style="font-weight:bold;margin-bottom:6px;font-size:14px;">${escapeHtml(pinTitle)}</div>
              ${pinBody ? `<div style="font-size:13px;color:#444;line-height:1.4;">${escapeHtml(pinBody)}</div>` : ""}
            </div>`;
            marker.bindPopup(popupContent, { closeButton: true });
          } else {
            marker.on("click", () => {
              const updatedPin = {
                ...pin,
                title: pinTitle,
                body: pinBody,
              };
              setInfo(updatedPin);
              setTimeout(() => toggleDrawer(true), 200);
            });
          }
        });
        /////////
        /////////
        // FeatureGroup and draw toolbar: only when pins are editable (students & read-only views see no toolbar)
        var drawnItems;
        if (canEditPins) {
          const existingCount = Array.isArray(geoJson?.pins) ? geoJson.pins.length : 0;
          drawnItems = new L.FeatureGroup();
          map.addLayer(drawnItems);
          drawnItemsRef.current = drawnItems;
          var drawControl = new L.Control.Draw({
            position: "topright",
            draw: {
              // Use a custom icon even while placing the marker to avoid the broken default icon
              marker: {
                icon: createSlidePinIcon(),
              },
              polygon: false,
              polyline: false,
              rectangle: false,
              circle: false,
              circlemarker: false,
            },
            edit: {
              featureGroup: drawnItems,
              edit: false,
              remove: true, // keep only the bin (delete) action
            },
          });
          map.addControl(drawControl);
        }
        if (!isEmpty(geoJson?.pins)) {
          let geoJsonPinIndex = 0;
          L.geoJSON(geoJson?.pins, {
            pointToLayer: (feature, latlng) => {
              geoJsonPinIndex++;
              return L.marker(latlng, { icon: createSlidePinIcon() });
            },
          })
            .addTo(map)
            .on(
              "click",
              function (e) {
                if (!canEditPins) return;
                const { layer } = e;

                const id = layer?.feature.properties.id;
                setInfo({ content: content[id], markerID: id });

                setTimeout(() => {
                  toggleDrawer(true);
                }, 200);
              }.bind()
            );
        }
        ///////////////
        ///////////////
        ///////////////
        const watermark = L.control({ position: "bottomright" });
        watermark.onAdd = function (map) {
          const div = L.DomUtil.create("div", "watermark");
          div.style.background = "transparent"; // Ensure background is transparent
          div.innerHTML = `<img src=${logo} alt="Microlab Watermark" style="width: 100px; height: auto;">`;
          return div;
        };
        watermark.addTo(map);
        ///////////////
        ///////////////
        ///////////////
        ////Edit and add functions (only when pins are editable)
        if (canEditPins && drawnItems) {
          map.on("draw:created", function (e) {
            var type = e.layerType,
              layer = e.layer;

            console.log("layer draw:created : ", layer);
            if (type === "marker") {
              // showModal(layer);
              // Do marker specific actions
              const latlng = layer.getLatLng(); // Get latitude and longitude of the marker

              // Use the same visual pin style as existing pins
              layer.setIcon(createSlidePinIcon());

              // Construct GeoJSON structure
              const newFeature = {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [latlng.lng, latlng.lat], // Leaflet uses lat/lng; GeoJSON uses lng/lat
                },
                properties: {
                  id: generateUniqueId(), // Replace with your logic to generate an ID
                },
              };

              setInfo(newFeature);
              pendingNewLayerRef.current = layer;
              // Open dialog immediately for new pin
              setTimeout(() => {
                toggleDrawer(true);
              }, 0);
            }
            layer.on("click", (e) => {
              toggleDrawer(true);
            });
            ///////////////
            ///////////////
            ///////////////
            // Do whatever else you need to. (save to db; add to map etc)
            drawnItems.addLayer(layer);
          });

          map.addLayer(drawnItems);
        }
        ///////////////
        ///////////////
        ///////////////


      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      drawnItemsRef.current = null;
      pendingNewLayerRef.current = null;
    };
  }, [tileURL, geoJson, lang]);

  const handleAddMaterial = async (event) => {
    if (activeDeleteButton) {
      const file_id = geoJson?.Files?.[0]?.id;
      const confirmation = await openAppConfirm({
        title: "Delete slide?",
        message: "Are you sure you want to delete this slide? Related pins will be removed as well.",
        confirmText: "Delete",
        cancelText: "Cancel",
        confirmColor: "error",
      });
      if (confirmation) {
        try {
          const delRes = await DeleteFile(file_id);
          if (delRes?.status === 200) {
            setGeoJson((prev) => ({ ...(prev || {}), pins: [], Files: [] }));
            setActiveDeleteButton(false);
            setTitleURL("");
            setToast({
              open: true,
              severity: "success",
              message: "Slide and related pins deleted successfully.",
            });
            await fetchSlide();
            return;
          }
          setToast({
            open: true,
            severity: "error",
            message: delRes?.message || "Delete failed. Please try again.",
          });
        } catch (err) {
          setToast({
            open: true,
            severity: "error",
            message: err?.message || "Delete failed. Please try again.",
          });
        }
      }
    } else {
      const file = event.target.files[0];
      if (file) {
        try {
          setShowLoader(true);
          setUploadPercentage(0);
          setUploadStatusText("Uploading ZIP:");
          // Handle `.zip` file upload
          const formdata = new FormData();
          formdata.append("file", file);
          formdata.append("entity_type", "slide");
          formdata.append("entity_id", id);
          formdata.append("destination", "slides");
          formdata.append("folder_name", "slides");

          const access_token = store.getState().auth?.access_token;
          let myHeaders = new Headers();
          myHeaders.append("Authorization", access_token);

          myHeaders = {
            Authorization: access_token,
          };

          const response = await axios.post(`${basicURL}file/upload_lambda`, formdata, {
            headers: myHeaders,
            onUploadProgress: (progressEvent) => {
              const raw = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              const uploadWeighted = Math.round(raw * 0.7); // reserve last 30% for extraction phase
              setUploadPercentage(uploadWeighted);
            },
          });
          console.log("pippo response", response);
          const jobId = response?.data?.jobId;
          if (!jobId) {
            setShowLoader(false);
            setToast({
              open: true,
              severity: "warning",
              message: "Upload started, but tracking id is missing.",
            });
            return;
          }

          setUploadStatusText("Extracting ZIP on server:");
          const maxPolls = 450; // up to 15 minutes with 2s interval
          for (let i = 0; i < maxPolls; i++) {
            const statusRes = await axios.get(`${basicURL}file/upload_lambda/job/${jobId}`, {
              headers: myHeaders,
            });
            const extractPercent = Number(statusRes?.data?.extractPercent || 0);
            const uiPercent = Math.min(100, 70 + Math.round(extractPercent * 0.3));
            setUploadPercentage(uiPercent);

            if (statusRes?.data?.jobStatus === "complete") {
              setUploadPercentage(100);
              setShowLoader(false);
              const tileRootUrl =
                statusRes?.data?.result?.tile_root_url || response?.data?.tile_root_url;
              if (tileRootUrl) {
                setTitleURL(`${tileRootUrl}/{z}/{x}/{y}.png`);
              }
              await fetchSlide();
              setToast({
                open: true,
                severity: "success",
                message: "Slide uploaded and extracted successfully.",
              });
              return;
            }

            if (statusRes?.data?.jobStatus === "error") {
              throw new Error(statusRes?.data?.error || "Extraction failed.");
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
          }

          throw new Error("Extraction timed out. Please check server logs.");
        } catch (error) {
          console.error("Upload error:", error);
          setShowLoader(false);
          setUploadStatusText("uploadProgress:");
          setToast({
            open: true,
            severity: "error",
            message: error?.message || "Upload or extraction failed.",
          });
        }
      }
    }
  };

  const toggleDrawer = (open, cancelNew = false) => {
    if (!open && cancelNew && pendingNewLayerRef.current && drawnItemsRef.current) {
      try {
        drawnItemsRef.current.removeLayer(pendingNewLayerRef.current);
      } catch (e) {
        // ignore if already removed
      }
      pendingNewLayerRef.current = null;
    }
    setOpenDrawer(open);
  };
  const uploaderTitle = uploadStatusText;

  const isStudent = role === "student";

  const getSlideContextTitle = () => {
    const slideLocs = Array.isArray(geoJson?.slideLocalizations) ? geoJson.slideLocalizations : [];
    const subtitleLocs = Array.isArray(geoJson?.titleLocalizations) ? geoJson.titleLocalizations : [];
    const parentTitleLocs = Array.isArray(geoJson?.Titles?.titleLocalizations)
      ? geoJson.Titles.titleLocalizations
      : [];

    const pickLocalizedTitle = (locs) =>
      locs.find((loc) => loc?.field === "title" && loc?.language === lang)?.value ||
      locs.find((loc) => loc?.field === "title" && loc?.language === "en")?.value ||
      locs.find((loc) => loc?.field === "title")?.value;

    const localized =
      pickLocalizedTitle(slideLocs) ||
      pickLocalizedTitle(subtitleLocs) ||
      pickLocalizedTitle(parentTitleLocs);

    return (
      localized ||
      geoJson?.name ||
      geoJson?.parentTitle?.name ||
      geoJson?.Titles?.name ||
      geoJson?.titles?.name ||
      "histology"
    );
  };

  const setupDrawingCanvas = () => {
    const image = baseImageRef.current;
    const canvas = drawCanvasRef.current;
    if (!image || !canvas) return;
    const rect = image.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width));
    canvas.height = Math.max(1, Math.floor(rect.height));
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#ef4444";
  };

  const getRelativePoint = (event) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const onDrawStart = (event) => {
    event.preventDefault();
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getRelativePoint(event);
    drawingRef.current = true;
    lastPointRef.current = point;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const onDrawMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getRelativePoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    setHasDrawing(true);
  };

  const onDrawEnd = () => {
    drawingRef.current = false;
  };

  const clearDrawing = () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
  };

  const dataUrlToFile = async (dataUrl, fileName) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || "image/png" });
  };

  const buildAnnotatedImage = async () => {
    const base = baseImageRef.current;
    const drawCanvas = drawCanvasRef.current;
    if (!base || !drawCanvas) return null;

    const output = document.createElement("canvas");
    output.width = drawCanvas.width;
    output.height = drawCanvas.height;
    const ctx = output.getContext("2d");
    ctx.drawImage(base, 0, 0, output.width, output.height);
    ctx.drawImage(drawCanvas, 0, 0, output.width, output.height);
    return output.toDataURL("image/png");
  };

  const openAiDialog = async () => {
    setAiError("");
    setCaptureLoading(true);
    setAiOpen(true);
    try {
      const mapNode = document.getElementById("map");
      if (!mapNode) throw new Error("Map not found");
      const canvas = await html2canvas(mapNode, {
        useCORS: true,
        allowTaint: true,
        scale: window.devicePixelRatio > 1 ? 1.5 : 1,
      });
      setCapturedImage(canvas.toDataURL("image/png"));
      setHasDrawing(false);
      setTimeout(setupDrawingCanvas, 0);
      if (aiThreadId) {
        const historyRes = await getAIMessagesByThreadCall(aiThreadId);
        const normalized = Array.isArray(historyRes)
          ? historyRes
              .map((m) => ({
                sender: m?.sender === "ai" ? "ai" : "user",
                content: m?.content || "",
              }))
              .filter((m) => !!m.content)
          : [];
        setAiMessages(normalized);
      } else {
        setAiMessages([]);
      }
    } catch (error) {
      setAiError("Could not capture slide snapshot. Please try again.");
    } finally {
      setCaptureLoading(false);
    }
  };

  const sendAiQuestion = async () => {
    const question = aiQuestion.trim();
    if (!question || aiLoading) return;
    setAiLoading(true);
    setAiError("");
    const pendingUserMessage = { sender: "user", content: question };
    setAiMessages((prev) => [...prev, pendingUserMessage]);
    setAiQuestion("");

    try {
      const formData = new FormData();
      formData.append("body", question);
      formData.append("title", getSlideContextTitle());
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("slide_id", id);
      if (aiThreadId) {
        formData.append("thread_id", aiThreadId);
      }

      const annotated = await buildAnnotatedImage();
      if (annotated) {
        const file = await dataUrlToFile(annotated, `slide-${id}-annotation.png`);
        formData.append("file", file);
      }

      const res = await askAIChatCall(formData);
      if (res?.thread_id && !aiThreadId) {
        setAiThreadId(res.thread_id);
      }
      const aiContent = res?.messages?.[1]?.content?.trim();
      if (aiContent) {
        setAiMessages((prev) => [...prev, { sender: "ai", content: aiContent }]);
      } else {
        setAiMessages((prev) => [
          ...prev,
          { sender: "ai", content: "Sorry, I could not generate a response. Please try again." },
        ]);
      }
    } catch (error) {
      setAiMessages((prev) => [
        ...prev,
        { sender: "ai", content: "Sorry, request failed. Please try again." },
      ]);
      setAiError("Failed to send question to AI.");
    } finally {
      setAiLoading(false);
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (!capturedImage) return;
    const onResize = () => setupDrawingCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [capturedImage]);

  const totalTiles = Math.max(1, Number(gridRows) * Number(gridCols));
  const capturedCount = Object.values(capturedTiles || {}).filter((t) => t?.done).length;
  const capturePercent = Math.round((capturedCount / totalTiles) * 100);
  const currentRow = Math.floor(currentTile / gridCols);
  const currentCol = currentTile % gridCols;
  const currentTileKey = `${currentRow}-${currentCol}`;

  const classifyOverlap = (score) => {
    if (score == null) return { label: "", severity: "info" };
    if (score >= 75) return { label: "Good alignment", severity: "success" };
    if (score >= 55) return { label: "Acceptable, refine a bit", severity: "warning" };
    return { label: "Risk of gap/shift, realign before capture", severity: "error" };
  };

  const toGray = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;

  const buildGrayStrip = (imgData, w, h) => {
    const out = new Float32Array(w * h);
    const src = imgData.data;
    for (let i = 0, p = 0; i < src.length; i += 4, p += 1) {
      out[p] = toGray(src[i], src[i + 1], src[i + 2]);
    }
    return out;
  };

  const compareGrayStrips = (a, b) => {
    if (!a || !b || a.length !== b.length || a.length === 0) return null;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) diff += Math.abs(a[i] - b[i]);
    const meanDiff = diff / a.length;
    return Math.max(0, Math.min(100, Math.round(100 - (meanDiff / 255) * 100)));
  };

  const getReferenceInfoForCurrentTile = () => {
    const leftKey = `${currentRow}-${currentCol - 1}`;
    const topKey = `${currentRow - 1}-${currentCol}`;
    if (currentCol > 0 && capturedTiles[leftKey]) {
      return { tile: capturedTiles[leftKey], direction: "horizontal" };
    }
    if (currentRow > 0 && capturedTiles[topKey]) {
      return { tile: capturedTiles[topKey], direction: "vertical" };
    }
    return { tile: null, direction: null };
  };

  const extractStripFromImage = async (imageUrl, direction) => {
    if (!imageUrl || !direction) return { stripUrl: "", stripGray: null };
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return { stripUrl: "", stripGray: null };
    const canvas = document.createElement("canvas");
    const strip = 80;
    if (direction === "horizontal") {
      canvas.width = strip;
      canvas.height = h;
    } else {
      canvas.width = w;
      canvas.height = strip;
    }
    const ctx = canvas.getContext("2d");
    if (direction === "horizontal") {
      ctx.drawImage(img, w - strip, 0, strip, h, 0, 0, strip, h);
    } else {
      ctx.drawImage(img, 0, h - strip, w, strip, 0, 0, w, strip);
    }
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return {
      stripUrl: canvas.toDataURL("image/jpeg", 0.85),
      stripGray: buildGrayStrip(imgData, canvas.width, canvas.height),
    };
  };

  const stopCaptureCamera = () => {
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    } catch (_) {
      // ignore
    }
    mediaStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  };

  // Try to stabilize camera behavior across captures (best-effort; browser/device dependent).
  const stabilizeCameraTrack = async (track) => {
    if (!track || typeof track.getCapabilities !== "function") return;
    try {
      const caps = track.getCapabilities() || {};
      const settings = typeof track.getSettings === "function" ? track.getSettings() || {} : {};
      const advanced = {};

      // Keep zoom fixed when supported (prevents accidental zoom jumps).
      if (caps.zoom) {
        const currentZoom =
          typeof settings.zoom === "number"
            ? settings.zoom
            : typeof caps.zoom.min === "number"
              ? caps.zoom.min
              : 1;
        advanced.zoom = currentZoom;
      }

      // Prefer stable/manual-like modes when supported.
      if (Array.isArray(caps.focusMode)) {
        if (caps.focusMode.includes("manual")) {
          advanced.focusMode = "manual";
          if (caps.focusDistance) {
            advanced.focusDistance =
              typeof settings.focusDistance === "number"
                ? settings.focusDistance
                : (Number(caps.focusDistance.min || 0) + Number(caps.focusDistance.max || 0)) / 2;
          }
        } else if (caps.focusMode.includes("single-shot")) {
          advanced.focusMode = "single-shot";
        }
      }
      if (Array.isArray(caps.exposureMode) && caps.exposureMode.includes("manual")) {
        advanced.exposureMode = "manual";
        if (caps.exposureTime && typeof settings.exposureTime === "number") {
          advanced.exposureTime = settings.exposureTime;
        }
        if (caps.iso && typeof settings.iso === "number") {
          advanced.iso = settings.iso;
        }
      }
      if (Array.isArray(caps.whiteBalanceMode) && caps.whiteBalanceMode.includes("manual")) {
        advanced.whiteBalanceMode = "manual";
        if (caps.colorTemperature && typeof settings.colorTemperature === "number") {
          advanced.colorTemperature = settings.colorTemperature;
        }
      }

      if (Object.keys(advanced).length > 0) {
        await track.applyConstraints({ advanced: [advanced] });
      }
    } catch (_) {
      // Ignore unsupported constraints.
    }
  };

  const startCaptureCamera = async () => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCaptureFeedback(
          "Camera API unavailable in this browser/context. Use HTTPS (or localhost) and a modern browser."
        );
        setToast({
          open: true,
          severity: "error",
          message: "Camera is not available in this browser/context.",
        });
        return false;
      }
      if (!window.isSecureContext) {
        setCaptureFeedback(
          "HTTP mode detected. Live preview is blocked on insecure HTTP; use fallback capture button."
        );
        setToast({
          open: true,
          severity: "info",
          message: "Using fallback capture on HTTP (no live preview).",
        });
        return false;
      }

      stopCaptureCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      mediaStreamRef.current = stream;
      const [videoTrack] = stream.getVideoTracks();
      if (videoTrack) {
        await stabilizeCameraTrack(videoTrack);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      setCaptureFeedback("Camera ready. Align microscope eyepiece and capture current tile.");
      return true;
    } catch (error) {
      const msg = String(error?.message || "");
      const denied =
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError" ||
        /denied|permission/i.test(msg);
      const notFound = error?.name === "NotFoundError" || /not found|no camera/i.test(msg);
      const detail = denied
        ? "Camera permission denied. Allow camera access in browser settings."
        : notFound
          ? "No camera device was found."
          : "Camera access failed. Please allow camera permission and retry.";
      setCaptureFeedback(detail);
      setToast({
        open: true,
        severity: "error",
        message: detail,
      });
      return false;
    }
  };

  const computeBlurScore = (ctx, w, h) => {
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
      gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const i = y * w + x;
        const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w];
        sum += lap;
        sumSq += lap * lap;
        n += 1;
      }
    }
    if (n === 0) return 0;
    const mean = sum / n;
    return Math.max(0, (sumSq / n) - mean * mean);
  };

  // Compare current tile against nearest already-captured neighbor.
  // Very high similarity usually means stage/camera didn't move enough.
  const computeTileSimilarityAgainstNeighbor = async (tileCanvas, neighborUrl) => {
    if (!tileCanvas || !neighborUrl) return null;
    try {
      const size = 96;
      const toGrayGrid = (canvasLike) => {
        const c = document.createElement("canvas");
        c.width = size;
        c.height = size;
        const ctx = c.getContext("2d");
        ctx.drawImage(canvasLike, 0, 0, size, size);
        const img = ctx.getImageData(0, 0, size, size);
        const out = new Float32Array(size * size);
        for (let i = 0, p = 0; i < img.data.length; i += 4, p += 1) {
          out[p] = 0.299 * img.data[i] + 0.587 * img.data[i + 1] + 0.114 * img.data[i + 2];
        }
        return out;
      };

      const neighborImg = new Image();
      neighborImg.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        neighborImg.onload = resolve;
        neighborImg.onerror = reject;
        neighborImg.src = neighborUrl;
      });
      const neighborCanvas = document.createElement("canvas");
      neighborCanvas.width = neighborImg.naturalWidth || neighborImg.width || size;
      neighborCanvas.height = neighborImg.naturalHeight || neighborImg.height || size;
      neighborCanvas.getContext("2d").drawImage(neighborImg, 0, 0);

      const a = toGrayGrid(tileCanvas);
      const b = toGrayGrid(neighborCanvas);
      let diff = 0;
      for (let i = 0; i < a.length; i += 1) diff += Math.abs(a[i] - b[i]);
      const meanDiff = diff / a.length;
      return Math.max(0, Math.min(100, Math.round(100 - (meanDiff / 255) * 100)));
    } catch (_) {
      return null;
    }
  };

  /**
   * Crop camera frame to the currently selected grid cell (edge/corner based on row/col),
   * so each uploaded tile is a distinct part of the live frame.
   */
  const getCropRectForCurrentTile = (frameWidth, frameHeight) => {
    const safeCols = Math.max(1, Number(gridCols) || 1);
    const safeRows = Math.max(1, Number(gridRows) || 1);
    const col = Math.max(0, Math.min(safeCols - 1, Number(currentCol) || 0));
    const row = Math.max(0, Math.min(safeRows - 1, Number(currentRow) || 0));

    const baseW = Math.floor(frameWidth / safeCols);
    const baseH = Math.floor(frameHeight / safeRows);
    const x = col * baseW;
    const y = row * baseH;
    const width = col === safeCols - 1 ? frameWidth - x : baseW;
    const height = row === safeRows - 1 ? frameHeight - y : baseH;
    return { x, y, width, height };
  };

  const fetchCaptureSessionConfig = async () => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    const res = await axios.get(`${basicURL}file/slides/${id}/capture-session`, { headers });
    return res?.data?.data || null;
  };

  const saveCaptureSessionConfig = async (payload) => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    await axios.put(`${basicURL}file/slides/${id}/capture-session`, payload, { headers });
  };

  const loadCapturedTilesForSession = async (
    sessionId,
    preferredCurrentTile = null,
    rowsForSession = gridRows,
    colsForSession = gridCols
  ) => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    try {
      const res = await axios.get(
        `${basicURL}file/slides/${id}/tiles?session_id=${encodeURIComponent(sessionId)}`,
        { headers }
      );
      const mapped = {};
      (res?.data?.tiles || []).forEach((tile) => {
        const key = `${tile.row}-${tile.col}`;
        mapped[key] = {
          id: tile.id,
          url: tile.url,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        };
      });
      setCapturedTiles(mapped);
      const localTotalTiles = Math.max(1, Number(rowsForSession) * Number(colsForSession));
      let firstMissing = 0;
      for (let i = 0; i < localTotalTiles; i += 1) {
        const r = Math.floor(i / colsForSession);
        const c = i % colsForSession;
        if (!mapped[`${r}-${c}`]) {
          firstMissing = i;
          break;
        }
        firstMissing = i + 1;
      }
      if (
        Number.isInteger(preferredCurrentTile) &&
        preferredCurrentTile >= 0 &&
        preferredCurrentTile < localTotalTiles
      ) {
        setCurrentTile(preferredCurrentTile);
      } else {
        setCurrentTile(Math.min(firstMissing, localTotalTiles - 1));
      }
    } catch (_) {
      setCapturedTiles({});
      const localTotalTiles = Math.max(1, Number(rowsForSession) * Number(colsForSession));
      if (
        Number.isInteger(preferredCurrentTile) &&
        preferredCurrentTile >= 0 &&
        preferredCurrentTile < localTotalTiles
      ) {
        setCurrentTile(preferredCurrentTile);
      } else {
        setCurrentTile(0);
      }
    }
  };

  const openCaptureGrid = async () => {
    try {
      const persisted = await fetchCaptureSessionConfig();
      const sessionId =
        persisted?.session_id || captureSessionId || `${id}-${Date.now()}`;
      const persistedRows = Number(persisted?.grid_rows);
      const persistedCols = Number(persisted?.grid_cols);
      const persistedTile = Number(persisted?.current_tile);

      const effectiveRows =
        Number.isInteger(persistedRows) && persistedRows >= 2 ? persistedRows : gridRows;
      const effectiveCols =
        Number.isInteger(persistedCols) && persistedCols >= 2 ? persistedCols : gridCols;
      setGridRows(effectiveRows);
      setGridCols(effectiveCols);
      if (typeof persisted?.ghost_enabled === "boolean") setGhostEnabled(persisted.ghost_enabled);
      if (Number.isInteger(Number(persisted?.ghost_opacity))) {
        setGhostOpacity(Math.max(0, Math.min(100, Number(persisted.ghost_opacity))));
      }
      setCaptureSessionId(sessionId);
      setCaptureGridOpen(true);
      setCaptureFeedback("");
      setNeedsRetake(false);
      setOverlapScore(null);
      setOverlapHint("");
      setReferenceStripUrl("");
      setLiveStripUrl("");
      referenceStripDataRef.current = null;

      const started = await startCaptureCamera();
      if (!started) return;
      await loadCapturedTilesForSession(
        sessionId,
        Number.isInteger(persistedTile) ? persistedTile : null,
        effectiveRows,
        effectiveCols
      );
      setIsCaptureSessionHydrated(true);
      if (!initialGuideSeen) {
        setInitialGuideSeen(true);
        setCaptureFeedback("Align your mobile camera with microscope eyepiece. Capture tile by tile.");
      }
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Failed to load capture session.",
      });
    }
  };

  const closeCaptureGrid = () => {
    setCaptureGridOpen(false);
    stopCaptureCamera();
    setNeedsRetake(false);
    setOverlapScore(null);
    setOverlapHint("");
    setReferenceStripUrl("");
    setLiveStripUrl("");
    referenceStripDataRef.current = null;
  };

  useEffect(() => {
    if (!captureGridOpen || !isCaptureSessionHydrated || !captureSessionId) return undefined;
    if (captureSessionSaveTimerRef.current) clearTimeout(captureSessionSaveTimerRef.current);
    captureSessionSaveTimerRef.current = setTimeout(() => {
      saveCaptureSessionConfig({
        session_id: captureSessionId,
        grid_rows: Number(gridRows),
        grid_cols: Number(gridCols),
        current_tile: Number(currentTile),
        ghost_enabled: !!ghostEnabled,
        ghost_opacity: Number(ghostOpacity),
      }).catch(() => {});
    }, 300);
    return () => {
      if (captureSessionSaveTimerRef.current) {
        clearTimeout(captureSessionSaveTimerRef.current);
      }
    };
  }, [
    captureGridOpen,
    isCaptureSessionHydrated,
    captureSessionId,
    gridRows,
    gridCols,
    currentTile,
    ghostEnabled,
    ghostOpacity,
  ]);

  const captureCurrentTile = async () => {
    if (!videoRef.current || !cameraReady || captureBusy) return;
    const video = videoRef.current;
    const fullCanvas = captureCanvasRef.current || document.createElement("canvas");
    captureCanvasRef.current = fullCanvas;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    fullCanvas.width = w;
    fullCanvas.height = h;
    const fullCtx = fullCanvas.getContext("2d");
    fullCtx.drawImage(video, 0, 0, w, h);

    const crop = getCropRectForCurrentTile(w, h);
    const tileCanvas = document.createElement("canvas");
    tileCanvas.width = Math.max(1, crop.width);
    tileCanvas.height = Math.max(1, crop.height);
    const tileCtx = tileCanvas.getContext("2d");
    tileCtx.drawImage(
      fullCanvas,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      tileCanvas.width,
      tileCanvas.height
    );

    const blurScore = computeBlurScore(tileCtx, tileCanvas.width, tileCanvas.height);
    const isBlurry = blurScore < 80;

    if (isBlurry) {
      setNeedsRetake(true);
      setCaptureFeedback("Image looks blurry. Please retake this tile.");
      setToast({ open: true, severity: "warning", message: "Blurry image detected. Retake required." });
      return;
    }

    // Reject almost identical captures to force real stage movement tile-by-tile.
    if (referenceTile?.url) {
      const similarity = await computeTileSimilarityAgainstNeighbor(tileCanvas, referenceTile.url);
      if (similarity != null && similarity >= 97) {
        setNeedsRetake(true);
        setCaptureFeedback("Tile is too similar to previous one. Move stage/camera and recapture.");
        setToast({
          open: true,
          severity: "warning",
          message: "No movement detected between tiles. Please move and recapture.",
        });
        return;
      }
    }

    setCaptureBusy(true);
    const key = `${currentRow}-${currentCol}`;
    setCapturedTiles((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        status: "uploading",
        progress: 0,
        error: null,
      },
    }));
    try {
      const blob = await new Promise((resolve) => tileCanvas.toBlob(resolve, "image/jpeg", 0.98));
      const file = new File([blob], `tile_r${currentRow}_c${currentCol}.jpg`, {
        type: "image/jpeg",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("row", String(currentRow));
      formData.append("col", String(currentCol));
      formData.append("grid_rows", String(gridRows));
      formData.append("grid_cols", String(gridCols));
      formData.append("session_id", captureSessionId);
      formData.append("blur_score", String(Math.round(blurScore)));

      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(`${basicURL}file/slides/${id}/tiles`, formData, {
        headers,
        onUploadProgress: (event) => {
          const percent = event?.total
            ? Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
            : 0;
          setCapturedTiles((prev) => ({
            ...(prev || {}),
            [key]: {
              ...(prev?.[key] || {}),
              status: "uploading",
              progress: percent,
              error: null,
            },
          }));
        },
      });
      const tileUrl = res?.data?.tile?.url;
      const tileId = res?.data?.tile?.id || null;
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          id: tileId,
          url: tileUrl,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        },
      }));
      setNeedsRetake(false);
      setCaptureFeedback("Tile captured");

      if ("vibrate" in navigator) navigator.vibrate(40);
      const next = currentTile + 1;
      if (next < totalTiles) {
        setCurrentTile(next);
      } else {
        setToast({
          open: true,
          severity: "success",
          message: "All tiles captured. Ready for stitching pipeline.",
        });
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Tile upload failed.";
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          ...(prev?.[key] || {}),
          done: false,
          status: "error",
          progress: 0,
          error: errMsg,
        },
      }));
      setToast({
        open: true,
        severity: "error",
        message: errMsg,
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const uploadTileFile = async (file) => {
    if (!file || captureBusy) return;
    const key = `${currentRow}-${currentCol}`;
    setCaptureBusy(true);
    setCapturedTiles((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        status: "uploading",
        progress: 0,
        error: null,
      },
    }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("row", String(currentRow));
      formData.append("col", String(currentCol));
      formData.append("grid_rows", String(gridRows));
      formData.append("grid_cols", String(gridCols));
      formData.append("session_id", captureSessionId);
      formData.append("blur_score", "0");

      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(`${basicURL}file/slides/${id}/tiles`, formData, {
        headers,
        onUploadProgress: (event) => {
          const percent = event?.total
            ? Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
            : 0;
          setCapturedTiles((prev) => ({
            ...(prev || {}),
            [key]: {
              ...(prev?.[key] || {}),
              status: "uploading",
              progress: percent,
              error: null,
            },
          }));
        },
      });
      const tileUrl = res?.data?.tile?.url;
      const tileId = res?.data?.tile?.id || null;
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          id: tileId,
          url: tileUrl,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        },
      }));
      setNeedsRetake(false);
      setCaptureFeedback("Tile captured");
      if ("vibrate" in navigator) navigator.vibrate(40);

      const next = currentTile + 1;
      if (next < totalTiles) {
        setCurrentTile(next);
      } else {
        setToast({
          open: true,
          severity: "success",
          message: "All tiles captured. Ready for stitching pipeline.",
        });
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Tile upload failed.";
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          ...(prev?.[key] || {}),
          done: false,
          status: "error",
          progress: 0,
          error: errMsg,
        },
      }));
      setToast({
        open: true,
        severity: "error",
        message: errMsg,
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const removeCurrentTile = async () => {
    const tile = capturedTiles?.[currentTileKey];
    if (!tile) return;
    if (captureBusy) return;
    setCaptureBusy(true);
    try {
      if (tile?.id) {
        const res = await DeleteFile(tile.id);
        if (!(res?.status === 200 || res?.status === 204)) {
          throw new Error(res?.message || "Failed to delete tile.");
        }
      }
      setCapturedTiles((prev) => {
        const next = { ...(prev || {}) };
        delete next[currentTileKey];
        return next;
      });
      setNeedsRetake(true);
      setCaptureFeedback("Tile deleted. Capture this tile again.");
      setToast({ open: true, severity: "success", message: "Tile deleted." });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "Failed to delete tile.",
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const startStitchPipeline = async () => {
    if (!captureSessionId || stitchBusy || captureBusy) return;
    setStitchBusy(true);
    try {
      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(
        `${basicURL}file/slides/${id}/capture-session/stitch`,
        {
          session_id: captureSessionId,
          grid_rows: Number(gridRows),
          grid_cols: Number(gridCols),
          compress_enabled: true,
          compress_quality: 95,
        },
        { headers }
      );
      const jobId = res?.data?.jobId || "";
      setStitchJobId(jobId);
      setStitchStatus("processing");
      setStitchPipelinePercent(10);
      setStitchPipelinePhase("queued");
      setStitchPipelineMessage("Queued");
      setToast({
        open: true,
        severity: "success",
        message: jobId
          ? `Stitch started (job: ${jobId.slice(0, 8)}...).`
          : "Stitch pipeline started.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Failed to start stitch pipeline.",
      });
    } finally {
      setStitchBusy(false);
    }
  };

  useEffect(() => {
    if (!stitchJobId) return undefined;
    let cancelled = false;

    const pollStatus = async () => {
      try {
        const access_token = store.getState().auth?.access_token;
        const headers = { Authorization: access_token };
        const res = await axios.get(
          `${basicURL}file/slides/${id}/capture-session/stitch/${stitchJobId}`,
          { headers }
        );
        if (cancelled) return;
        const status = String(res?.data?.jobStatus || "").toLowerCase();
        setStitchStatus(status || "processing");
        const pipeline = res?.data?.pipeline || {};
        if (typeof pipeline?.percent === "number") {
          setStitchPipelinePercent(Math.max(0, Math.min(100, pipeline.percent)));
        }
        if (pipeline?.phase) setStitchPipelinePhase(String(pipeline.phase));
        if (pipeline?.message) setStitchPipelineMessage(String(pipeline.message));
        if (status === "complete") {
          setToast({
            open: true,
            severity: "success",
            message: "Compress & tile completed. Slide reloaded.",
          });
          setStitchJobId("");
          setStitchStatus("");
          setStitchPipelinePercent(0);
          setStitchPipelinePhase("");
          setStitchPipelineMessage("");
          setCaptureGridOpen(false);
          stopCaptureCamera();
          fetchSlide();
          return;
        }
        if (status === "error") {
          setToast({
            open: true,
            severity: "error",
            message: res?.data?.error || res?.data?.message || "Compress & tile failed.",
          });
          setStitchJobId("");
          setStitchStatus("");
          setStitchPipelinePercent(0);
          setStitchPipelinePhase("");
          setStitchPipelineMessage("");
        }
      } catch (_) {
        // keep polling; transient errors are expected
      }
    };

    pollStatus();
    const timer = setInterval(pollStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [stitchJobId, id]);

  const handleFallbackCaptureFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadTileFile(file);
    event.target.value = "";
  };

  useEffect(() => {
    if (!captureGridOpen) return;
    const ref = getReferenceInfoForCurrentTile();
    setReferenceTile(ref.tile);
    setReferenceDirection(ref.direction);
    setOverlapScore(null);
    setOverlapHint("");
  }, [captureGridOpen, currentTile, capturedTiles, gridCols]);

  useEffect(() => {
    let cancelled = false;
    const buildReferenceStrip = async () => {
      if (!referenceTile?.url || !referenceDirection) {
        setReferenceStripUrl("");
        referenceStripDataRef.current = null;
        return;
      }
      try {
        const { stripUrl, stripGray } = await extractStripFromImage(
          referenceTile.url,
          referenceDirection
        );
        if (cancelled) return;
        setReferenceStripUrl(stripUrl);
        referenceStripDataRef.current = stripGray;
      } catch (_) {
        if (cancelled) return;
        setReferenceStripUrl("");
        referenceStripDataRef.current = null;
      }
    };
    buildReferenceStrip();
    return () => {
      cancelled = true;
    };
  }, [referenceTile, referenceDirection]);

  useEffect(() => {
    if (!captureGridOpen || !cameraReady || !referenceDirection || !referenceStripDataRef.current) {
      return;
    }
    if (!window.isSecureContext || !videoRef.current) return;

    const interval = setInterval(() => {
      try {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) return;
        const canvas = liveStripCanvasRef.current || document.createElement("canvas");
        liveStripCanvasRef.current = canvas;
        const w = video.videoWidth;
        const h = video.videoHeight;
        const strip = 80;
        if (referenceDirection === "horizontal") {
          canvas.width = strip;
          canvas.height = h;
        } else {
          canvas.width = w;
          canvas.height = strip;
        }
        const ctx = canvas.getContext("2d");
        if (referenceDirection === "horizontal") {
          // Current tile should align its left edge with reference tile right edge.
          ctx.drawImage(video, 0, 0, strip, h, 0, 0, strip, h);
        } else {
          // Current tile should align its top edge with reference tile bottom edge.
          ctx.drawImage(video, 0, 0, w, strip, 0, 0, w, strip);
        }
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const liveGray = buildGrayStrip(imgData, canvas.width, canvas.height);
        const score = compareGrayStrips(referenceStripDataRef.current, liveGray);
        setOverlapScore(score);
        const cls = classifyOverlap(score);
        setOverlapHint(cls.label);
        setLiveStripUrl(canvas.toDataURL("image/jpeg", 0.8));
      } catch (_) {
        // ignore transient frame errors
      }
    }, 700);

    return () => clearInterval(interval);
  }, [captureGridOpen, cameraReady, referenceDirection, referenceStripUrl]);

  useEffect(() => () => stopCaptureCamera(), []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="content">
        <CardHeader>
          <CardTitle tag="h5">
            {isStudent ? (
              <MDButton
                style={{ marginBottom: 15, marginRight: 10 }}
                color={"primary"}
                variant="contained"
                onClick={openAiDialog}
              >
                Ask AI
              </MDButton>
            ) : null}
            {canEditSlide && !activeDeleteButton ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleAddMaterial}
                />
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"info"}
                  variant="contained"
                  onClick={() => fileInputRef.current.click()} // Trigger the hidden file input
                >
                  Upload Slide
                </MDButton>
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"secondary"}
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={openCaptureGrid}
                >
                  Capture Slide
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background: "rgba(244, 67, 54, 0.18)",
                      color: "#f44336",
                    }}
                  >
                    Bita
                  </span>
                </MDButton>
              </div>
            ) : canEditSlide && activeDeleteButton ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"primary"}
                  variant="contained"
                  onClick={() => handleAddMaterial()}
                >
                  Delete Slide
                </MDButton>
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"secondary"}
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={openCaptureGrid}
                >
                  Capture Slide
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background: "rgba(244, 67, 54, 0.18)",
                      color: "#f44336",
                    }}
                  >
                    Bita
                  </span>
                </MDButton>
              </div>
            ) : isPublicSlide ? (
              <span style={{ marginBottom: 15, color: "#666", fontSize: 14 }}>
                View only — only admin can upload or delete public slides.
              </span>
            ) : null}
          </CardTitle>
          {showLoader && (
            <CardBody>
              <Form>
                <div>
                  <div>
                    {uploaderTitle} {Math.round(uploadProgress)}%
                  </div>
                  <div
                    style={{ marginBottom: 20, height: "10px", backgroundColor: "#ddd" }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${uploadProgress}%`,
                        backgroundColor: "green",
                      }}
                    ></div>
                  </div>
                </div>
              </Form>
            </CardBody>
          )}
        </CardHeader>

        <Row>
          <Col md="12">
            <Card className="card-user">

              <CardBody>
                <Form>
                  <div id={"map"} style={{ width: "100%", height: "70vh" }} />
                  <CustomDrawer
                    data={info}
                    lang={lang}
                    slide_id={id}
                    fetchSlide={fetchSlide}
                    open={drawerOpen}
                    toggleDrawer={toggleDrawer}
                  />
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Dialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pr: 6 }}>
            Ask AI About This Slide
            <IconButton
              onClick={() => setAiOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {aiError ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {aiError}
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Draw on the slide, then ask your question. The marked image is sent with your prompt.
            </Typography>

            <Box
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: 1,
                overflow: "hidden",
                border: "1px solid #ddd",
                mb: 1.5,
                minHeight: 200,
                touchAction: "none",
                background: "#f7f7f7",
              }}
            >
              {captureLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 220 }}>
                  <CircularProgress size={26} />
                </Box>
              ) : capturedImage ? (
                <>
                  <img
                    ref={baseImageRef}
                    src={capturedImage}
                    alt="Slide snapshot"
                    style={{ width: "100%", display: "block" }}
                    onLoad={setupDrawingCanvas}
                  />
                  <canvas
                    ref={drawCanvasRef}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "crosshair" }}
                    onPointerDown={onDrawStart}
                    onPointerMove={onDrawMove}
                    onPointerUp={onDrawEnd}
                    onPointerLeave={onDrawEnd}
                  />
                </>
              ) : null}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
              <MDButton color="secondary" variant="outlined" onClick={clearDrawing} disabled={!hasDrawing}>
                Clear drawing
              </MDButton>
              <Typography variant="caption" color="text.secondary">
                Context: {getSlideContextTitle()}
              </Typography>
            </Box>

            <Paper
              variant="outlined"
              ref={chatMessagesRef}
              sx={{
                p: 1.5,
                maxHeight: isMobile ? "32vh" : 260,
                overflowY: "auto",
                backgroundColor: "#fafafa",
                mb: 1.5,
              }}
            >
              {aiMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Start a conversation about this slide.
                </Typography>
              ) : (
                aiMessages.map((msg, index) => (
                  <Box
                    key={`${msg.sender}-${index}`}
                    sx={{
                      display: "flex",
                      justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        px: msg.sender === "ai" ? 0 : 1.2,
                        py: msg.sender === "ai" ? 0 : 0.9,
                        padding: msg.sender === "ai" ? "8px" : undefined,
                        borderRadius: 1.2,
                        maxWidth: "85%",
                        bgcolor: msg.sender === "user" ? "#1976d2" : "#e9ecef",
                        color: msg.sender === "user" ? "#fff" : "#222",
                        fontSize: 14,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {msg.sender === "ai"
                        ? parse(msg.content || "")
                        : msg.content}
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Ask about this slide..."
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendAiQuestion();
                  }
                }}
                multiline
                minRows={1}
                maxRows={4}
              />
              <MDButton
                color="info"
                variant="contained"
                onClick={sendAiQuestion}
                disabled={aiLoading || !aiQuestion.trim() || captureLoading}
              >
                {aiLoading ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
              </MDButton>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog
          open={captureGridOpen}
          onClose={closeCaptureGrid}
          fullScreen={isMobile}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pr: 6 }}>
            Capture Slide
            <span
              style={{
                marginLeft: 10,
                padding: "2px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(244, 67, 54, 0.18)",
                color: "#f44336",
                verticalAlign: "middle",
              }}
            >
              Bita
            </span>
            <IconButton
              onClick={closeCaptureGrid}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1.5, flexWrap: "wrap" }}>
              <Typography variant="caption">Rows</Typography>
              <Slider
                value={gridRows}
                onChange={(_, v) => setGridRows(Number(v))}
                min={2}
                max={10}
                step={1}
                valueLabelDisplay="auto"
                sx={{ width: 120 }}
              />
              <Typography variant="caption">Cols</Typography>
              <Slider
                value={gridCols}
                onChange={(_, v) => setGridCols(Number(v))}
                min={2}
                max={10}
                step={1}
                valueLabelDisplay="auto"
                sx={{ width: 120 }}
              />
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {capturedCount}/{totalTiles} ({capturePercent}%)
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={capturePercent}
              sx={{ height: 8, borderRadius: 999, mb: 1.5 }}
            />

            <Box
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.18)",
                bgcolor: "#000",
                aspectRatio: "4/3",
                "@keyframes tilePulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(0,229,255,0.8)" },
                  "70%": { boxShadow: "0 0 0 8px rgba(0,229,255,0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(0,229,255,0)" },
                },
              }}
            >
              <video
                ref={videoRef}
                muted
                playsInline
                autoPlay
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {ghostEnabled && referenceTile?.url ? (
                <img
                  src={referenceTile.url}
                  alt="Ghost alignment"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: Math.max(0, Math.min(1, ghostOpacity / 100)),
                    pointerEvents: "none",
                    mixBlendMode: "screen",
                  }}
                />
              ) : null}

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                  gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                }}
              >
                {Array.from({ length: totalTiles }).map((_, idx) => {
                  const r = Math.floor(idx / gridCols);
                  const c = idx % gridCols;
                  const key = `${r}-${c}`;
                  const tileMeta = capturedTiles[key];
                  const done = !!tileMeta?.done;
                  const uploading = tileMeta?.status === "uploading";
                  const hasError = tileMeta?.status === "error";
                  const active = idx === currentTile;
                  return (
                    <Box
                      key={key}
                      onClick={() => setCurrentTile(idx)}
                      sx={{
                        cursor: "pointer",
                        border: "1px solid rgba(255,255,255,0.35)",
                        bgcolor: hasError
                          ? "rgba(231,76,60,0.3)"
                          : uploading
                            ? "rgba(52,152,219,0.28)"
                            : done
                          ? "rgba(46,204,113,0.24)"
                          : active
                            ? "rgba(0,229,255,0.18)"
                            : "rgba(0,0,0,0.12)",
                        position: "relative",
                        ...(active
                          ? {
                              border: "2px solid #00e5ff",
                              animation: "tilePulse 1.2s infinite",
                            }
                          : {}),
                      }}
                    >
                      {done ? (
                        <CheckCircleIcon
                          sx={{ position: "absolute", right: 2, bottom: 2, fontSize: 14, color: "#2ecc71" }}
                        />
                      ) : null}
                      {uploading ? (
                        <CircularProgress
                          size={14}
                          sx={{ position: "absolute", right: 2, bottom: 2, color: "#4fc3f7" }}
                        />
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.2,
                alignItems: "center",
                mt: 1,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <MDButton
                color={ghostEnabled ? "info" : "secondary"}
                variant={ghostEnabled ? "contained" : "outlined"}
                onClick={() => setGhostEnabled((v) => !v)}
                sx={{ borderRadius: 999, minWidth: 130 }}
                disabled={!referenceTile?.url}
              >
                {ghostEnabled ? "Ghost On" : "Ghost Off"}
              </MDButton>
              <Typography variant="caption">Ghost opacity</Typography>
              <Slider
                value={ghostOpacity}
                onChange={(_, v) => setGhostOpacity(Number(v))}
                min={0}
                max={100}
                step={5}
                valueLabelDisplay="auto"
                sx={{ width: 140 }}
                disabled={!ghostEnabled || !referenceTile?.url}
              />
              {overlapScore != null ? (
                <Alert
                  severity={classifyOverlap(overlapScore).severity}
                  sx={{ py: 0, px: 1, borderRadius: 999, ml: "auto" }}
                >
                  {overlapHint} ({overlapScore}%)
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  Capture first neighbor tile to start alignment scoring.
                </Typography>
              )}
            </Box>

            {referenceStripUrl && liveStripUrl ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
                <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                  <Typography variant="caption" sx={{ px: 1, py: 0.3, display: "block", bgcolor: "#f5f5f5" }}>
                    Reference edge
                  </Typography>
                  <img
                    src={referenceStripUrl}
                    alt="Reference edge"
                    style={{ width: "100%", maxHeight: 80, objectFit: "cover", display: "block" }}
                  />
                </Box>
                <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                  <Typography variant="caption" sx={{ px: 1, py: 0.3, display: "block", bgcolor: "#f5f5f5" }}>
                    Live edge
                  </Typography>
                  <img
                    src={liveStripUrl}
                    alt="Live edge"
                    style={{ width: "100%", maxHeight: 80, objectFit: "cover", display: "block" }}
                  />
                </Box>
              </Box>
            ) : null}

            {captureFeedback ? (
              <Typography
                variant="body2"
                sx={{ mt: 1.2, color: needsRetake ? "warning.main" : "success.main", fontWeight: 600 }}
              >
                {captureFeedback}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: "block" }}>
              Tile {currentRow + 1},{currentCol + 1} —{" "}
              {capturedTiles?.[currentTileKey]?.status === "uploading"
                ? `Uploading ${capturedTiles?.[currentTileKey]?.progress || 0}%`
                : capturedTiles?.[currentTileKey]?.status === "error"
                  ? capturedTiles?.[currentTileKey]?.error || "Upload failed"
                  : capturedTiles?.[currentTileKey]?.done
                    ? "Uploaded"
                    : "Pending"}
            </Typography>
            {stitchJobId ? (
              <Box sx={{ mt: 0.8 }}>
                <Typography variant="caption" color="info.main" sx={{ display: "block", mb: 0.4 }}>
                  Stitch job: {stitchJobId} {stitchStatus ? `(${stitchStatus})` : ""}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={stitchPipelinePercent}
                  sx={{ height: 8, borderRadius: 999 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: "block" }}>
                  {stitchPipelinePhase ? `${stitchPipelinePhase.toUpperCase()} — ` : ""}
                  {stitchPipelineMessage || "Processing..."} ({stitchPipelinePercent}%)
                </Typography>
              </Box>
            ) : null}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
              <input
                ref={fallbackCameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={handleFallbackCaptureFile}
              />
              <MDButton
                color={needsRetake ? "warning" : "info"}
                variant="contained"
                disabled={!cameraReady || captureBusy}
                onClick={captureCurrentTile}
                sx={{
                  minWidth: 170,
                  minHeight: 56,
                  borderRadius: 999,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {captureBusy ? (
                  <CircularProgress size={22} color="inherit" />
                ) : needsRetake ? (
                  "Retake"
                ) : (
                  "Capture"
                )}
              </MDButton>
              <MDButton
                color="error"
                variant="outlined"
                disabled={captureBusy || !capturedTiles?.[currentTileKey]?.done}
                onClick={removeCurrentTile}
                sx={{
                  minWidth: 150,
                  minHeight: 56,
                  borderRadius: 999,
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                Delete Tile
              </MDButton>
              {capturedCount === totalTiles ? (
                <MDButton
                  color="success"
                  variant="contained"
                  disabled={captureBusy || stitchBusy || !!stitchJobId}
                  onClick={startStitchPipeline}
                  sx={{
                    minWidth: 190,
                    minHeight: 56,
                    borderRadius: 999,
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {stitchBusy || stitchStatus === "processing" ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Compress & Tile"
                  )}
                </MDButton>
              ) : null}
              {!window.isSecureContext ? (
                <MDButton
                  color="info"
                  variant="outlined"
                  disabled={captureBusy}
                  onClick={() => fallbackCameraInputRef.current?.click()}
                  sx={{
                    ml: 1.2,
                    minWidth: 170,
                    minHeight: 56,
                    borderRadius: 999,
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  Open Camera
                </MDButton>
              ) : null}
            </Box>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={4500}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%", boxShadow: 3, borderRadius: 1.5 }}
          >
            {toast.message}
          </Alert>
        </Snackbar>

      </div>
    </DashboardLayout>
  );
}

const Model = ({ fbxUrl, onLoad }) => {
  const fbx = useLoader(FBXLoader, fbxUrl, (loader) => {
    loader.manager.onLoad = onLoad;
  });

  return <primitive object={fbx} />;
};

export default ShowTile;
